// Each round shows one market scenario: a ticker, a headline, and a short
// price path. The player must call BUY, HOLD, or SELL before the timer bar
// empties. Every scenario is pre-authored with one objectively-labeled
// ideal action — never a live random walk — so the "right call" is teaching
// a real pattern (don't panic-sell a temporary dip, don't chase a hype
// spike, do act on verified, durable bad news) rather than rewarding luck.
export const SCENARIOS = [
  {
    id: "mp1",
    ticker: "PXL",
    headline: "A trusted long-term holding dips 4% on general market jitters — no news specific to the company.",
    pricePath: [86, 85, 83, 84, 83],
    idealAction: "HOLD",
    stakes: "LOW",
    rationale:
      "A few percent of daily noise with no company-specific news isn't a signal — it's just a normal day in the market. Selling here trades a temporary wiggle for a real, locked-in loss.",
  },
  {
    id: "mp2",
    ticker: "QRT",
    headline: "Price has been flat for weeks on light trading volume, with no earnings report or news due for another month.",
    pricePath: [40, 40, 41, 40, 40],
    idealAction: "HOLD",
    stakes: "LOW",
    rationale:
      "Nothing has actually changed. Trading in and out on boredom alone usually just adds fees and taxes without adding any real edge.",
  },
  {
    id: "mp3",
    ticker: "GLM",
    headline: "A viral social media post sends the stock up 24% today with no earnings, no new product, and no company statement behind it.",
    pricePath: [102, 110, 120, 126, 128],
    idealAction: "HOLD",
    stakes: "MEDIUM",
    rationale:
      "Hype without fundamentals is what a pump looks like from the inside. Buying in after a 24% spike on vibes alone is how you end up buying the top.",
  },
  {
    id: "mp4",
    ticker: "DXR",
    headline: "The broader market drops 6% today after a surprise rate announcement — no news about this specific company.",
    pricePath: [58, 56, 54, 55, 54],
    idealAction: "HOLD",
    stakes: "MEDIUM",
    rationale:
      "A market-wide move driven by macro news isn't a verdict on this one company. Panic-selling into a broad dip is how a paper loss becomes a real, permanent one.",
  },
  {
    id: "mp5",
    ticker: "VNT",
    headline: "A headline reads \"Insider Selling Reported\" — the filing shows it's a pre-scheduled, routine sale under a trading plan set up months ago.",
    pricePath: [72, 71, 72, 71, 72],
    idealAction: "HOLD",
    stakes: "HIGH",
    rationale:
      "Scheduled insider sales (10b5-1 plans) are set up in advance for personal reasons — a mortgage, diversification, taxes — and say nothing new about the company. Reacting to the scary headline instead of the actual filing is the trap.",
  },
  {
    id: "mp6",
    ticker: "ORB",
    headline: "Stock has climbed for three straight days with no news, and social chatter is turning into full-blown FOMO.",
    pricePath: [140, 148, 155, 160, 163],
    idealAction: "HOLD",
    stakes: "HIGH",
    rationale:
      "A run with no fundamental driver is momentum, not information. Chasing it after three green days is exactly the setup that leaves latecomers holding the top.",
  },
  {
    id: "mp7",
    ticker: "STL",
    headline: "A steady, diversified index fund dips 5% along with the rest of the market during a routine pullback.",
    pricePath: [210, 205, 201, 203, 204],
    idealAction: "BUY",
    stakes: "LOW",
    rationale:
      "A broad, diversified fund dipping with the market is exactly when a scheduled, steady contribution buys more shares for the same dollar — the core idea behind dollar-cost averaging.",
  },
  {
    id: "mp8",
    ticker: "FRM",
    headline: "A well-run dividend-paying company just announced its 12th consecutive annual dividend increase, alongside steady, unspectacular growth.",
    pricePath: [64, 64, 65, 65, 66],
    idealAction: "BUY",
    stakes: "LOW",
    rationale:
      "A long, unbroken streak of dividend increases is a real signal of durable cash flow and management discipline — not hype, just consistency compounding quietly.",
  },
  {
    id: "mp9",
    ticker: "NDL",
    headline: "Company reports earnings that beat expectations and raises its full-year guidance, but the stock is only up 4% so far.",
    pricePath: [88, 88, 90, 91, 92],
    idealAction: "BUY",
    stakes: "MEDIUM",
    rationale:
      "Improved fundamentals — a real earnings beat plus raised guidance — that the market hasn't fully priced in yet is a genuinely different situation than chasing a headline-driven spike.",
  },
  {
    id: "mp10",
    ticker: "CVR",
    headline: "The stock drops 9% in sympathy after a competitor issues a profit warning, even though this company just reaffirmed its own guidance.",
    pricePath: [51, 48, 46, 47, 47],
    idealAction: "BUY",
    stakes: "MEDIUM",
    rationale:
      "A sell-off dragging down a healthy company because of someone else's bad news is a classic overreaction — the kind of gap between price and fundamentals a disciplined buyer looks for.",
  },
  {
    id: "mp11",
    ticker: "AGX",
    headline: "The broad market is down 18% from its recent high in a well-known seasonal correction, with no sign of a recession in the underlying economic data.",
    pricePath: [95, 88, 82, 80, 83],
    idealAction: "BUY",
    stakes: "HIGH",
    rationale:
      "Historically, market corrections without a recession behind them have tended to recover — continuing to invest steadily through the dip is how dollar-cost averaging actually buys more shares while they're cheaper.",
  },
  {
    id: "mp12",
    ticker: "TSN",
    headline: "A company's new flagship product launches to strong reviews and an analyst upgrade, but the stock has only moved up 3% — the market hasn't caught up yet.",
    pricePath: [130, 131, 133, 134, 134],
    idealAction: "BUY",
    stakes: "HIGH",
    rationale:
      "A real, verified improvement to the business that the price hasn't fully reflected yet is different from chasing a stock that already ran. The catalyst here is genuine, not hype.",
  },
  {
    id: "mp13",
    ticker: "PLM",
    headline: "Company misses earnings badly, cuts full-year guidance in half, and reports negative free cash flow for the second straight quarter.",
    pricePath: [34, 30, 27, 25, 24],
    idealAction: "SELL",
    stakes: "LOW",
    rationale:
      "Deteriorating fundamentals across multiple real metrics — not just one bad headline — is a genuine reason to reassess, not just noise to ride out.",
  },
  {
    id: "mp14",
    ticker: "HZD",
    headline: "The company loses its single largest customer, worth 40% of annual revenue, with no comparable replacement announced.",
    pricePath: [45, 40, 36, 34, 33],
    idealAction: "SELL",
    stakes: "LOW",
    rationale:
      "Losing nearly half of revenue permanently is a structural change to the business, not a temporary dip — this is the kind of news dollar-cost averaging isn't designed to ride through.",
  },
  {
    id: "mp15",
    ticker: "QBT",
    headline: "An independent investigation confirms the CEO falsified financial statements for the past two years; the SEC opens a formal inquiry.",
    pricePath: [80, 55, 42, 38, 35],
    idealAction: "SELL",
    stakes: "MEDIUM",
    rationale:
      "Confirmed, verified fraud at the top of a company is a fundamentally different situation from a scary-sounding headline — it's a real reason the original investment thesis no longer holds.",
  },
  {
    id: "mp16",
    ticker: "WVX",
    headline: "The company's core product is confirmed to be replaced by a rival's superior technology; sales have fallen 35% for three consecutive quarters.",
    pricePath: [58, 49, 41, 37, 33],
    idealAction: "SELL",
    stakes: "MEDIUM",
    rationale:
      "A sustained, multi-quarter sales collapse tied to a real competitive loss — not a single bad day — is exactly the kind of durable, verified deterioration worth acting on.",
  },
  {
    id: "mp17",
    ticker: "LDG",
    headline: "The company breaches a major debt covenant and hires restructuring advisors amid real bankruptcy risk.",
    pricePath: [22, 16, 11, 8, 6],
    idealAction: "SELL",
    stakes: "HIGH",
    rationale:
      "A debt covenant breach with restructuring advisors already engaged is about as unambiguous a real solvency risk as it gets — this isn't a dip to ride out.",
  },
  {
    id: "mp18",
    ticker: "KTV",
    headline: "An independent audit finds the company has been shipping empty boxes to inflate reported sales for over a year.",
    pricePath: [66, 40, 25, 18, 14],
    idealAction: "SELL",
    stakes: "HIGH",
    rationale:
      "Confirmed accounting fraud erases the credibility of every number the original investment case was built on — this is a real, verified reason to exit, not panic.",
  },
];

export const ACTIONS = [
  { id: "BUY", label: "Buy", hint: "Get in", accent: "cyan" },
  { id: "HOLD", label: "Hold", hint: "Sit tight", accent: "gold" },
  { id: "SELL", label: "Sell", hint: "Get out", accent: "magenta" },
];

export const STAKES_POINTS = { LOW: 40, MEDIUM: 70, HIGH: 110 };

export const STARTING_LIVES = 3;
export const SCENARIOS_PER_LEVEL = 8;
export const BASE_TIME_MS = 4500;
export const MIN_TIME_MS = 1800;
export const TIME_STEP_PER_LEVEL = 250;

export function pickScenario(lastId) {
  let next = SCENARIOS[Math.floor(Math.random() * SCENARIOS.length)];
  // Avoid immediate repeats so it doesn't feel stale.
  let guard = 0;
  while (next.id === lastId && guard < 6) {
    next = SCENARIOS[Math.floor(Math.random() * SCENARIOS.length)];
    guard += 1;
  }
  return next;
}

export function timeLimitForLevel(lvl) {
  return Math.max(MIN_TIME_MS, BASE_TIME_MS - (lvl - 1) * TIME_STEP_PER_LEVEL);
}

export function computeGain(stakes, combo) {
  const comboMultiplier = 1 + combo * 0.25;
  return Math.max(1, Math.round(STAKES_POINTS[stakes] * comboMultiplier));
}
